<template>
  <div class="login-form">
    <h1 class="login-form__title">
      {{ $isPure ? '登录' : '欢迎登录有言服务平台' }}
    </h1>

    <template v-if="!$isPure">
      <div v-if="!getEnv('privatization')" class="sub">
        <span v-if="activeKey === 'phone'"> 未注册的用户验证后自动创建账号 </span>
        <span v-else class="sub__register">
          <div>没有账号？</div>
          <Text variant="12" color="p6" class="cursor-pointer" @click="onClickRegister">
            立即注册
          </Text>
        </span>
      </div>
    </template>

    <Tabs
      v-if="!$isPure"
      v-model="activeKey"
      :items="tabItems"
      :gap="'32px'"
      class="login-form__tabs"
      @change="changeKey"
    />

    <Form v-model:form-ref="formRef" class="login-form__form" @submit="onFinish">
      <template v-if="activeKey === 'phone'">
        <FormField v-model="formState.mobile" name="mobile">
          <InputField placeholder="请输入手机号" :height="44" class="login-input" />
        </FormField>
        <FormField v-model="formState.sms_code" name="sms_code">
          <CaptchaInput
            v-model="formState.sms_code"
            placeholder="请输入验证码"
            :height="44"
            :is-send="isSend"
            :times="times"
            input-class="login-input login-form__captcha-input"
            button-class="login-form__captcha-button"
            @send-code="handleSendCode"
          />
        </FormField>
      </template>

      <template v-if="activeKey === 'password'">
        <FormField v-model="formState.login_name" name="login_name">
          <InputField :placeholder="accountHint" :height="44" class="login-input" />
        </FormField>
        <FormField v-model="formState.password" name="password">
          <InputField
            type="password"
            placeholder="请输入密码"
            :height="44"
            show-password-toggle
            class="login-input"
          />
        </FormField>
      </template>

      <div
        :class="[
          'login-form__buttons',
          !$isPure && activeKey === 'password' ? 'show-forget-password' : '',
        ]"
      >
        <Button
          variant="purple"
          size="large"
          :disabled="!canSubmit || loading"
          type="submit"
          :loading="loading"
        >
          {{ activeKey === 'password' ? '登录' : '登录 / 注册' }}
        </Button>

        <Button
          v-if="!$isPure && activeKey === 'password'"
          :variant="'text-purple'"
          @click="onOpenReset"
        >
          忘记密码
        </Button>

        <Button v-if="showWechatLogin" variant="border-back" size="large" @click="emits('switch')">
          <template #icon>
            <IconWechat class="login-form__switch-mode-icon" />
          </template>
          微信扫码登录
        </Button>
      </div>
    </Form>

    <ProtocolCheckbox
      v-if="!$isPure"
      :use-checkbox="true"
      v-model="formState.remember"
      user-agreement-url="https://frontend-protocol.xmov.ai/youyan-v3/user"
      privacy-policy-url="https://frontend-protocol.xmov.ai/youyan-v3/privacy"
      user-agreement-text="《用户协议》"
      privacy-policy-text="《隐私政策》"
      prefix-text="我已阅读并同意"
      middle-text="和"
    />
  </div>
</template>
<script lang="ts" setup>
// API 相关
import { getRegisterCode, getUserInfo, loginByAccount, loginByPhone } from '@/api';
import type { LoginInfo } from '~/api/interface';

// Hooks
import { useSendCode } from '@/hooks/useSendCode';

// 工具函数
import { legacyPasswordReg, phoneReg, smsCodeReg, transformPublicKey } from '@/utils/form';
import { getEnv } from '~/utils/env';
import { sessionGetItem, sessionRemoveItem } from '~/utils/localStorage';
import { emitBroadcast, TAB_ID } from '~/utils/postMessage';
import { buriedLogin, TRACK_EVENT, trackEvent, USER_TYPE } from '~/utils/track';

// 组件库
import { message } from '@rendora/common/composables/message';
import type { Account } from '@rendora/common/entities/user';
import { useUserStore } from '@rendora/common/store';
import IconWechat from '@rendora/design/assets/color/wechat.svg?component';
import { Button } from '@rendora/design/components/button';
import { Form, FormField } from '@rendora/design/components/form';
import { InputField } from '@rendora/design/components/input';
import { Tabs } from '@rendora/design/components/tabs';
import { Text } from '@rendora/design/components/text';
import CaptchaInput from '@rendora/design/widgets/captcha-input/index.vue';

// 常量
import { BROADCAST_KEY, INVITE_CODE, SESSION_SOURCE } from '~/constants/config';

// 业务逻辑
import { useAccountLock } from '../../../shared/lib/useAccountLock';
import { useProtocolConfirm } from '../../../shared/lib/useProtocolConfirm';
import { trackData } from '../lib/trackData';
import { useLoginTiming } from '../lib/useLoginTiming';
import ProtocolCheckbox from './ProtocolCheckbox.vue';

// ==================== 类型定义 ====================
interface FormState {
  mobile: string;
  login_name: string;
  sms_code: string;
  password: string;
  remember: boolean;
}

type LoginType = 'phone' | 'password';

// ==================== Composables ====================
const { $isPure } = useNuxtApp();
const { sendCode, isSend, times } = useSendCode();
const { isMobile } = useDevice();
const { show: showProtocolConfirm } = useProtocolConfirm();
const { setAccountList, setSession, setUserInfo } = useChangeUser();
const {
  recordClickSubmit,
  recordLoginRequestStart,
  recordLoginRequestEnd,
  getTimingData,
  reset: resetTiming,
} = useLoginTiming();

// ==================== 工具函数 ====================
const getDefaultLoginMode = (): LoginType => {
  if ($isPure) {
    return 'password';
  }
  return getEnv('privatization') ? 'password' : 'phone';
};

// ==================== 状态定义 ====================
const formState = reactive<FormState>({
  mobile: '',
  login_name: '',
  sms_code: '',
  password: '',
  remember: false,
});

const loading = ref(false);
const formRef = ref();
const activeKey = ref<LoginType>(getDefaultLoginMode());

// ==================== Emits ====================
const emits = defineEmits(['back', 'success', 'accountSelect', 'switch']);

// ==================== 计算属性 ====================
const accountHint = computed(() => (!$isPure ? '请输入手机号/账号' : '请输入账号'));

const tabItems = computed(() => {
  const items = [];
  if (!getEnv('privatization')) {
    items.push({
      key: 'phone',
      label: '验证码登录',
    });
  }
  items.push({
    key: 'password',
    label: '密码登录',
  });
  return items;
});

const canSubmit = computed(() => {
  if (activeKey.value === 'phone') {
    return !!(formState.sms_code && formState.mobile);
  }
  return !!(formState.password && formState.login_name);
});

const showWechatLogin = computed(() => {
  if ($isPure || isMobile) {
    return false;
  }
  return !getEnv('privatization');
});

// ==================== 错误处理函数 ====================
const handleLoginError = (
  error: {
    code?: number | string;
    error_code?: number | string;
    msg?: string;
    error_reason?: string;
    message?: string;
    data?: {
      remaining_num?: number;
      expired_time?: string;
    };
  },
  loginType: LoginType,
) => {
  const timingData = getTimingData();
  const errorCode = error?.code || error?.error_code || 'unknown';
  const errorMsg = error?.msg || error?.error_reason || error?.message || '未知错误';

  // 记录具体登录方式的失败埋点
  const trackLoginFail = (errorType: string, extraData?: Record<string, unknown>) => {
    const event =
      loginType === 'phone' ? TRACK_EVENT.LOGIN_PHONE_FAIL : TRACK_EVENT.LOGIN_PASSWORD_FAIL;

    trackEvent(event, {
      error_code: errorCode,
      error_msg: errorMsg,
      error_type: errorType,
      ...timingData,
      ...extraData,
    });
  };

  // 根据错误类型记录不同的失败埋点
  if (errorCode === 10003) {
    // 密码错误
    trackEvent(TRACK_EVENT.LOGIN_FAIL_PASSWORD_ERROR, {
      login_type: loginType,
      remaining_num: error?.data?.remaining_num,
      ...timingData,
    });
    trackLoginFail('password_error', {
      remaining_num: error?.data?.remaining_num,
    });
  } else if (errorCode === 10002) {
    // 账号锁定
    trackEvent(TRACK_EVENT.LOGIN_FAIL_ACCOUNT_LOCKED, {
      login_type: loginType,
      expired_time: error?.data?.expired_time,
      ...timingData,
    });
    trackLoginFail('account_locked', {
      expired_time: error?.data?.expired_time,
    });
  } else if (errorCode === 10001 || errorCode === 'account_not_exist') {
    // 账号不存在
    trackEvent(TRACK_EVENT.LOGIN_FAIL_ACCOUNT_NOT_EXIST, {
      login_type: loginType,
      error_code: errorCode,
      ...timingData,
    });
    trackLoginFail('account_not_exist');
  } else if (
    errorCode === 'verify_code_error' ||
    errorMsg?.includes('验证码') ||
    errorMsg?.includes('验证码错误')
  ) {
    // 验证码错误
    trackEvent(TRACK_EVENT.LOGIN_FAIL_VERIFY_CODE_ERROR, {
      login_type: loginType,
      error_code: errorCode,
      ...timingData,
    });
    trackLoginFail('verify_code_error');
  } else if (errorCode === 'verify_code_expired' || errorMsg?.includes('验证码过期')) {
    // 验证码过期
    trackEvent(TRACK_EVENT.LOGIN_FAIL_VERIFY_CODE_EXPIRED, {
      login_type: loginType,
      error_code: errorCode,
      ...timingData,
    });
    trackLoginFail('verify_code_expired');
  } else if (
    errorCode === 'network_error' ||
    error?.message?.includes('network') ||
    error?.message?.includes('网络')
  ) {
    // 网络错误
    trackEvent(TRACK_EVENT.LOGIN_FAIL_NETWORK_ERROR, {
      login_type: loginType,
      ...timingData,
    });
    trackLoginFail('network_error');
  } else {
    // 未知错误
    trackEvent(TRACK_EVENT.LOGIN_FAIL_UNKNOWN_ERROR, {
      login_type: loginType,
      error_code: errorCode,
      error_msg: errorMsg,
      ...timingData,
    });
    trackLoginFail('unknown_error');
    // 通用失败埋点
    trackEvent(TRACK_EVENT.LOGIN_FAIL, {
      login_type: loginType,
      error_code: errorCode,
      error_msg: errorMsg,
      ...timingData,
    });
  }
};

// ==================== 事件处理函数 ====================
const handleSendCode = async () => {
  if (isSend.value) return;

  if (!formState.mobile || !phoneReg.test(formState.mobile)) {
    message.warning('请输入正确的手机号');
    return;
  }

  if (!formState.remember && !$isPure) {
    message.warning('请先勾选服务规范');
    return;
  }

  try {
    const res = await getRegisterCode({
      mobile: formState.mobile,
    });

    if (!res.code) {
      sendCode();
      message.success('验证码发送成功');
    }
  } catch (error) {
    console.error('发送验证码失败:', error);
  }
};

const changeKey = (key: string) => {
  const typedKey = key as LoginType;
  const oldKey = activeKey.value;
  activeKey.value = typedKey;
  // 切换登录方式时重置时间追踪
  resetTiming();
  // 埋点：切换登录方式
  if (typedKey === 'phone') {
    trackEvent(TRACK_EVENT.LOGIN_SWITCH_TO_PHONE);
  } else if (typedKey === 'password') {
    trackEvent(TRACK_EVENT.LOGIN_SWITCH_TO_PASSWORD);
  }
  trackEvent(TRACK_EVENT.LOGIN_SWITCH_MODE, {
    from: oldKey,
    to: typedKey,
  });
};

const onClickRegister = () => {
  trackEvent(TRACK_EVENT.YOUYAN_LOGIN_CLICK_REGISTER);
  changeKey('phone');
};

const onOpenReset = () => {
  trackEvent(TRACK_EVENT.CLICK_LOGIN_FORGET_BTN);
  emits('back');
};

const confirm = async () => {
  formState.remember = true;
  await onFinish(toRaw(formState));
};

// ==================== 表单验证 ====================
const validateForm = (formValues: FormState): string[] => {
  const errors: string[] = [];

  if (activeKey.value === 'phone') {
    // 校验手机号
    if (!formValues.mobile) {
      errors.push('请输入手机号');
    } else if (!phoneReg.test(formValues.mobile)) {
      errors.push('请输入正确的手机号');
    }

    // 校验验证码
    if (!formValues.sms_code) {
      errors.push('请输入验证码');
    } else if (!smsCodeReg.test(formValues.sms_code)) {
      errors.push('请输入6位数字验证码');
    }
  } else {
    // 校验账号
    if (!formValues.login_name) {
      errors.push('请输入手机号/账号');
    }

    // 校验密码
    if (!formValues.password) {
      errors.push('请输入密码');
    } else if (!legacyPasswordReg.test(formValues.password)) {
      errors.push('密码为6-20位任意数字、字母、特殊符号');
    }
  }

  return errors;
};

// ==================== 登录处理 ====================
const handleLoginSuccess = async (
  res: { data: LoginInfo[] | undefined },
  formValues: FormState,
) => {
  if (!res.data || res.data.length === 0) {
    return;
  }

  const { data } = res;
  const timingData = getTimingData();

  // 用户账号列表
  setAccountList(data);
  useUserStore().setAccountList(data as unknown as Account[]);

  if (data.length > 1) {
    // 埋点：账号选择
    trackEvent(TRACK_EVENT.LOGIN_ACCOUNT_SELECT, {
      account_count: data.length,
    });
    emits('accountSelect', data);
  } else {
    // 用户token
    await setSession(data[0]);
    emitBroadcast(BROADCAST_KEY.REFRESH, { tabId: TAB_ID });

    // 埋点：登录成功
    if (data?.[0]?.user_id) {
      buriedLogin(data[0].user_id);
      trackData(activeKey.value, formValues, {
        NewUser: data[0]?.is_new_user ? USER_TYPE.NEW : USER_TYPE.OLD,
      });
    }

    // 埋点：登录成功
    if (activeKey.value === 'phone') {
      trackEvent(TRACK_EVENT.LOGIN_PHONE_SUCCESS, {
        account_name: formValues.mobile,
        NewUser: data[0]?.is_new_user ? USER_TYPE.NEW : USER_TYPE.OLD,
        ...timingData,
      });
    } else {
      trackEvent(TRACK_EVENT.LOGIN_PASSWORD_SUCCESS, {
        account_name: formValues.login_name,
        NewUser: data[0]?.is_new_user ? USER_TYPE.NEW : USER_TYPE.OLD,
        ...timingData,
      });
    }

    const result = await getUserInfo();
    // 用户信息
    if (result.data) {
      setUserInfo(result.data);
      emits('success', result.data);
    }
  }
  sessionRemoveItem(SESSION_SOURCE);
  loading.value = false;
};

const onFinish = async (values?: FormState) => {
  const formValues = values || formState;

  // 表单验证
  const validationErrors = validateForm(formValues);
  if (validationErrors.length > 0) {
    trackEvent(TRACK_EVENT.LOGIN_FORM_VALIDATE_ERROR, {
      login_type: activeKey.value,
      error: validationErrors[0],
    });
    message.error(validationErrors[0]);
    return;
  }

  // 协议确认
  if (!formState.remember && !$isPure) {
    showProtocolConfirm(confirm);
    return;
  }

  // 记录点击提交按钮时间
  const clickSubmitDuration = recordClickSubmit();

  // 埋点：点击登录按钮
  trackEvent(TRACK_EVENT.LOGIN_CLICK_SUBMIT, {
    login_type: activeKey.value,
    account_name: activeKey.value === 'phone' ? formValues.mobile : formValues.login_name,
    click_submit_duration: clickSubmitDuration,
  });

  // 埋点：开始登录流程
  trackEvent(TRACK_EVENT.LOGIN_START, {
    login_type: activeKey.value,
  });

  loading.value = true;
  recordLoginRequestStart();

  try {
    const linkCode = sessionGetItem(INVITE_CODE) || undefined;

    let res;
    if (activeKey.value === 'phone') {
      res = await loginByPhone({
        ...formValues,
      });
      if (linkCode && res.code === 0) {
        sessionRemoveItem(INVITE_CODE);
      }
    } else {
      const [encrypted, rsa] = await transformPublicKey({
        login_name: formValues.login_name,
        password: formValues.password,
      });

      res = await loginByAccount(
        {
          rsa_key_id: rsa?.id,
          auth_data: encrypted,
        },
        {
          autoErrorHandle: false,
        },
      );

      if (res.code !== 0) {
        return useAccountLock(res);
      }

      if (linkCode && res.code === 0) {
        sessionRemoveItem(INVITE_CODE);
      }
    }

    recordLoginRequestEnd();

    if (res.data) {
      await handleLoginSuccess(res, formValues);
    }
  } catch (error) {
    recordLoginRequestEnd();
    handleLoginError(
      error as {
        code?: number | string;
        error_code?: number | string;
        msg?: string;
        error_reason?: string;
        message?: string;
        data?: {
          remaining_num?: number;
          expired_time?: string;
        };
      },
      activeKey.value,
    );
    resetTiming();
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;

.login-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 328px;
  @include mobile {
    width: responsive-size(335px);
  }
}

.login-form__title {
  font-size: var(--x-text-24-size);
  font-weight: var(--x-weight-medium);
  line-height: var(--x-text-24-line-height);
  text-align: center;
  color: var(--x-text-1);
  margin-bottom: 8px;

  @include mobile {
    margin-bottom: responsive-size(8px);
  }
}

.sub {
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  color: var(--x-text-5);
  margin-bottom: 48px;
  @include mobile {
    margin-bottom: responsive-size(48px);
  }
}

.sub__register {
  display: flex;
  align-items: center;
  gap: 4px;

  @include mobile {
    gap: responsive-size(4px);
  }
}

.login-form__tabs {
  width: 100%;
  margin-bottom: 12px;

  @include mobile {
    margin-bottom: responsive-size(12px);
  }
}

.login-form__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @include mobile {
    gap: responsive-size(16px);
  }

  :deep(.form-item) {
    width: 100%;
    margin-bottom: 0;
  }
}

.login-input {
  border-radius: 8px;
  letter-spacing: 0.3px;
  width: 100%;
}

.login-form__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @include mobile {
    gap: responsive-size(16px);
  }

  :deep(.form-item) {
    width: 100%;
    margin-bottom: 0;
  }
}

.login-form__captcha-input {
  flex: 1;
  padding-right: 120px;

  @include mobile {
    padding-right: responsive-size(120px);
  }
}

.login-form__captcha-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  z-index: 1;
  padding: 0 8px;

  @include mobile {
    right: responsive-size(8px);
    padding: 0 responsive-size(8px);
  }

  &:hover {
    background-color: var(--x-purple-1);
  }

  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.97) !important;
  }
}

.login-form__switch-mode-icon {
  width: 20px;
  height: 20px;

  @include mobile {
    width: responsive-size(20px);
    height: responsive-size(20px);
  }
}

.login-form__buttons {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
  margin-top: 32px;

  @include mobile {
    gap: responsive-size(24px);
    margin-top: responsive-size(32px);
  }

  &.show-forget-password {
    gap: 8px;
    @include mobile {
      gap: responsive-size(16px);
      button:last-child {
        height: fit-content;
        padding: 0;
      }
    }
  }
}
</style>
